import { useState } from 'react';

const SECTIONS = [
  { id: 'overview', label: 'What is PARAM?' },
  { id: 'terms', label: 'Key Terms' },
  { id: 'workorders', label: 'Work Orders' },
  { id: 'purchaseorders', label: 'Purchase Orders' },
  { id: 'arinvoices', label: 'AR Invoices (Billing the Client)' },
  { id: 'apinvoices', label: 'AP Invoices (Paying Vendors)' },
  { id: 'payments', label: 'Recording Payments' },
  { id: 'dashboard', label: 'Using the Dashboard' },
  { id: 'import', label: 'Importing Hexaware Data' },
  { id: 'faq', label: 'Common Questions' },
];

export default function HelpPage() {
  const [active, setActive] = useState('overview');

  return (
    <div style={{ display: 'flex', gap: 32, padding: 28 }}>
      <div style={{ width: 220, flexShrink: 0 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>P.A.R.A.M. Help</h3>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              style={{
                textAlign: 'left', padding: '8px 10px', borderRadius: 'var(--radius)', border: 'none',
                background: active === s.id ? 'var(--info-bg)' : 'transparent',
                color: active === s.id ? 'var(--primary)' : 'var(--text)',
                fontWeight: active === s.id ? 600 : 400, fontSize: 13, cursor: 'pointer',
              }}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </div>

      <div style={{ flex: 1, maxWidth: 760, fontSize: 14, lineHeight: 1.7 }}>

        {active === 'overview' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>What is PARAM?</h2>
            <p><strong>P.A.R.A.M.</strong> stands for "Meticulous AR and AP utility for TMV" — it's TMV Global's
            internal accounting system for managing consultant staffing billing.</p>
            <p>Here's the core business model in one sentence: TMV places consultants with Hexaware (our client),
            bills Hexaware for their work (this is <strong>AR — Accounts Receivable</strong>, money coming <em>in</em>),
            and pays the staffing vendors who supply those consultants (this is <strong>AP — Accounts Payable</strong>,
            money going <em>out</em>). TMV sits in the middle, and the whole job of this system is to keep both
            sides — what we bill Hexaware and what we pay vendors — accurate and reconciled.</p>
            <p>Every consultant's engagement is tracked through a <strong>Work Order</strong>, and every month
            of real billable work gets its own <strong>Purchase Order</strong> and a matching pair of invoices
            (one AR, one AP).</p>
          </div>
        )}

        {active === 'terms' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>Key Terms</h2>
            <p><strong>Work Order (WO):</strong> represents one consultant's ongoing engagement with a client.
            Every WO has a real number (e.g. <code>CWK4632</code> for current engagements, or an older
            <code>A24866</code>-style number for historical ones). A WO has three possible types:</p>
            <ul>
              <li><strong>Contract Placement</strong> — the standard case: TMV bills the client monthly and pays
              the vendor monthly, for as long as the consultant is engaged.</li>
              <li><strong>Recurring Referral</strong> — TMV introduces the consultant and bills the client a referral
              fee every month (hours × referral rate). No vendor and no AP — TMV pays no one. A consultant can have
              two at once: the buyer side and the seller side.</li>
              <li><strong>FTE Placement</strong> — a one-time placement fee for a candidate hired directly onto
              the client's own payroll. This generates exactly one invoice, ever — no ongoing monthly billing.</li>
            </ul>
            <p><strong>Purchase Order (PO):</strong> the client's authorization to bill for a specific period.
            New-system WOs get a fresh PO every month; old-system WOs used one unified PO for the whole engagement.</p>
            <p><strong>AR Invoice:</strong> what TMV bills the client (Hexaware).<br/>
            <strong>AP Invoice:</strong> what TMV pays the vendor (or the consultant directly, if they're not
            working through a vendor).</p>
            <p><strong>Invoice Type</strong> (applies to both AR and AP):</p>
            <ul>
              <li><strong>Standard</strong> — regular hourly billing (Hours × Rate = Amount).</li>
              <li><strong>Allowance / Reimbursement</strong> — a flat amount for something like a shift allowance
              or an expense reimbursement (phone, travel, supplies), not tied to hours worked.</li>
              <li><strong>FTE Referral</strong> — the one-time placement fee (AR only — there's no vendor cost
              for a permanent placement).</li>
            </ul>
          </div>
        )}

        {active === 'workorders' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>Work Orders</h2>
            <p>A Work Order is the starting point for everything else. Before you can raise an invoice or create
            a PO for a consultant, they need a real Work Order in the system.</p>
            <p><strong>To create one:</strong> go to <em>Work Orders → New Work Order</em>, pick the contractor,
            the client, the engagement type, and (for Contract Placement) the vendor if the contractor is
            corp-to-corp. Set the real start date.</p>
            <p><strong>Rules the system enforces for each contractor</strong> (only <em>active</em> Work Orders
            count — closed or historical ones never block anything):</p>
            <ul>
              <li>At most <strong>2 active Recurring Referrals</strong> (the buyer side and the seller side).</li>
              <li><strong>No active Contract Placement alongside an active Recurring Referral</strong> — close one
              before the other becomes active.</li>
              <li><strong>FTE Placement</strong> never blocks and is never blocked.</li>
            </ul>
          </div>
        )}

        {active === 'purchaseorders' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>Purchase Orders</h2>
            <p>A PO represents the client's real authorization to bill for a period. You can create one from
            the Work Order's own page, or import them in bulk directly from a real Hexaware export file (see
            the "Importing Hexaware Data" section).</p>
            <p>You <strong>can</strong> create a PO against a Work Order that's already closed — this is
            expected when you're raising a late invoice for a period before the consultant left. The system
            checks that the period you enter genuinely falls within that WO's real date range, not whether the
            WO happens to be closed today.</p>
            <p><strong>PO numbers must be unique</strong> — if you try to create one with a number that already
            exists (even with slightly different spacing), the system will stop you and show which real PO
            already has that number.</p>
          </div>
        )}

        {active === 'arinvoices' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>AR Invoices — Billing the Client</h2>
            <p>Go to <em>AR Invoices → Create Invoice</em>. The very first thing you'll pick is
            <strong> Invoice Type</strong> — this changes what fields you'll see next:</p>
            <ul>
              <li><strong>Standard:</strong> pick the Work Order, enter the Period, then Hours and Rate — the
              Amount calculates automatically.</li>
              <li><strong>Allowance / Reimbursement:</strong> pick a Type (Allowance or Reimbursement) and a
              Category (e.g. "Shift Allowance", "Phone", "Taxi"), the Period, a Reference Note describing your
              proof (e.g. "Manager email attached"), and the flat Amount.</li>
              <li><strong>FTE Referral:</strong> the Work Order dropdown will only show FTE-type WOs. Picking
              one auto-fills the Date of Joining from that WO's real start date. Fill in the Offer Number,
              Employee ID, and the flat placement fee Amount.</li>
            </ul>
            <p>The Invoice # field can be left blank — it auto-generates in the standard
            <code> TMV/MMM/YYYY-NN</code> format (or <code>TMV-RF-MMM-YYYY-NN</code> for FTE Referral invoices).</p>
          </div>
        )}

        {active === 'apinvoices' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>AP Invoices — Paying Vendors</h2>
            <p>Go to <em>AP Invoices → Create Invoice</em>. Same Invoice Type selector as AR (Standard or
            Allowance/Reimbursement — there's no FTE option here, since placement fees don't have a
            corresponding vendor cost).</p>
            <p><strong>Invoice Number here always comes from the vendor's own real invoice</strong> — it's
            never auto-generated, since it needs to match what the vendor actually sent.</p>
            <p>For Standard invoices, the Purchase Order dropdown is filtered by the Period you enter — set
            the period first, and only POs that genuinely cover that period will show up.</p>
          </div>
        )}

        {active === 'payments' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>Recording Payments</h2>
            <p>An invoice's status (Paid, Partial, Sent) is calculated purely from <strong>verified</strong>
            payments recorded against it — not from a manual status field. Open the invoice and add a payment
            with the real payment date and amount.</p>
            <p>Real payment breakdown fields: <strong>Cash Amount</strong> is what actually moved, separate
            from <strong>EPD</strong> (Early Payment Discount) and <strong>BGC</strong> (background-check
            deduction) — these are tracked separately so the gross invoice amount always equals Cash + EPD + BGC.</p>
          </div>
        )}

        {active === 'dashboard' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>Using the Dashboard</h2>
            <p>The Dashboard's "Monthly Breakdown" cards show, for any month you pick, how many invoices should
            have been raised, how many actually were, and real payment totals. <strong>Click any number</strong>
            on these cards — it opens a full detail report in a new browser tab showing exactly which invoices
            or contractors are behind that number, with a CSV download option.</p>
            <p>Recurring Referrals are billed monthly, so they count in the monthly "To Be Raised" / "Drafted"
            figures like Contract Placements (no AP is expected for them). The "FTE Placements" section tracks
            one-time placement fees separately, since they don't fit the monthly billing pattern. It shows each placement's real <strong>Billable Date</strong>
            (Date of Joining + 90 days — the guarantee period after which the fee becomes billable) alongside
            its real invoice status.</p>
          </div>
        )}

        {active === 'import' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>Importing Hexaware Data</h2>
            <p>From <em>Purchase Orders → Import from Hexaware</em>, upload a real Hexaware PO export file.
            The system will match contractor names automatically and show you a full preview page — review
            each row, and you can directly edit the Period and Hours if they need adjusting before importing.</p>
            <p><strong>You don't have to import everything at once</strong> — use the checkbox on each row to
            select only the ones you want to import right now, and leave the rest unchecked for later.</p>
          </div>
        )}

        {active === 'faq' && (
          <div>
            <h2 style={{ marginBottom: 12 }}>Common Questions</h2>
            <p><strong>Why can't I create another active Work Order for this contractor?</strong> A contractor
            can have at most 2 active Recurring Referrals (buyer + seller side), and never an active Contract
            Placement alongside an active Recurring Referral. Only active Work Orders count — close the existing
            one first if this is a genuine new engagement.</p>
            <p><strong>Why does a closed Work Order still show up when I'm creating an invoice?</strong> This is
            intentional — you may need to raise a late invoice for a period before the contractor left.</p>
            <p><strong>Where do I void an invoice?</strong> This isn't available in the UI yet on the AR side
            — ask an admin to handle this directly for now.</p>
          </div>
        )}

      </div>
    </div>
  );
}
